import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { profileApi, resumeApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { formatBytes, humanise } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { ResumeParseResult } from '@/types/api'

interface Props {
  /** The chosen file; the dialog is open while one is set. */
  file: File | null
  onClose: () => void
  /** The upload went through (scanned or not) and the dialog was closed. */
  onDone?: () => void
  /** The file was stored but could not be read. */
  onFailed?: (message: string) => void
}

type Step =
  | { kind: 'ask' }
  | { kind: 'uploading'; scan: boolean }
  | { kind: 'review'; result: ResumeParseResult; found: string[] }
  | { kind: 'failed'; message: string }

const INVALIDATE = ['profile', 'resumes', 'profile-recommendations', 'onboarding'] as const
/** Footer buttons fill the width on a phone (the dialog is a bottom sheet there). */
const FOOTER_BUTTON = 'w-full sm:w-auto'

/**
 * Two steps around a resume upload: whether to scan it into the profile, and
 * — when scanned — which of the skills it found to add. Used on the profile
 * page and by the onboarding gate.
 */
export function ResumeScanModal({ file, onClose, onDone, onFailed }: Props) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>({ kind: 'ask' })
  const [selected, setSelected] = useState<string[]>([])

  // Skills already on the profile are not offered again.
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: profileApi.get,
    enabled: file !== null,
    retry: false,
  })

  useEffect(() => {
    setStep({ kind: 'ask' })
    setSelected([])
  }, [file])

  const finish = (message: string, detail?: string) => {
    for (const key of INVALIDATE) queryClient.invalidateQueries({ queryKey: [key] })
    toast.success(message, detail)
    onDone?.()
    onClose()
  }

  const upload = useMutation({
    mutationFn: ({ file, scan }: { file: File; scan: boolean }) =>
      resumeApi.upload(file, {
        makeDefault: true,
        applyToProfile: scan,
        ...(scan ? { applySkills: false } : {}),
      }),
    onMutate: ({ scan }) => setStep({ kind: 'uploading', scan }),
    onSuccess: (result, { scan }) => {
      if (result.resume.status === 'failed' || (scan && !result.parsed)) {
        setStep({
          kind: 'failed',
          message:
            result.resume.parseError ??
            "We couldn't read that file. It is saved, but nothing was filled in from it.",
        })
        return
      }
      if (!scan) {
        finish('Resume uploaded', 'It is now the default on applications.')
        return
      }
      const existing = new Set(
        (profileQuery.data?.skills ?? []).map((skill) => skill.name.trim().toLowerCase()),
      )
      const found: string[] = []
      for (const raw of result.parsed?.skills ?? []) {
        const name = raw.trim()
        const key = name.toLowerCase()
        if (!name || existing.has(key) || found.some((f) => f.toLowerCase() === key)) continue
        found.push(name)
      }
      setSelected(found)
      setStep({ kind: 'review', result, found })
    },
    onError: (error: ApiError) => setStep({ kind: 'failed', message: error.message }),
  })

  const addSkills = useMutation({
    mutationFn: (names: string[]) => profileApi.updateSkills(names.map((name) => ({ name }))),
    onSuccess: (_, names) =>
      finish(
        'Resume scanned',
        `${names.length} skill${names.length === 1 ? '' : 's'} added to your profile.`,
      ),
    onError: (error: ApiError) => toast.error('Could not add skills', error.message),
  })

  const dismiss = () => {
    if (step.kind === 'uploading') return
    if (step.kind === 'review') {
      finish('Resume scanned', 'Your profile was filled from it.')
      return
    }
    if (step.kind === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      onFailed?.(step.message)
    }
    onClose()
  }

  const open = file !== null
  const title =
    step.kind === 'review'
      ? 'We found new skills in your resume'
      : step.kind === 'failed'
        ? 'We could not read that file'
        : 'Scan resume for skills?'
  const description =
    step.kind === 'review'
      ? 'Review the skills and choose what to add to your profile.'
      : step.kind === 'ask' || step.kind === 'uploading'
        ? 'We can extract skills, work history and education from your resume and add them to your profile.'
        : undefined

  return (
    <Modal
      open={open}
      onClose={dismiss}
      title={title}
      description={description}
      size="md"
      dismissible={step.kind !== 'uploading'}
      footer={
        step.kind === 'ask' ? (
          <>
            <Button
              variant="secondary"
              className={FOOTER_BUTTON}
              onClick={() => file && upload.mutate({ file, scan: false })}
            >
              Upload only
            </Button>
            <Button className={FOOTER_BUTTON} onClick={() => file && upload.mutate({ file, scan: true })}>
              Scan and upload
            </Button>
          </>
        ) : step.kind === 'review' ? (
          <>
            <Button variant="secondary" className={FOOTER_BUTTON} onClick={dismiss} disabled={addSkills.isPending}>
              Skip
            </Button>
            <Button
              className={FOOTER_BUTTON}
              onClick={() => addSkills.mutate(selected)}
              loading={addSkills.isPending}
              disabled={selected.length === 0}
            >
              Add to profile
              {selected.length > 0 && (
                <span className="num">
                  {' '}
                  ({selected.length})
                </span>
              )}
            </Button>
          </>
        ) : step.kind === 'failed' ? (
          <Button variant="secondary" className={FOOTER_BUTTON} onClick={dismiss}>
            Close
          </Button>
        ) : undefined
      }
    >
      <div className="pb-4 pt-1">
        {file && (step.kind === 'ask' || step.kind === 'uploading') && (
          <div className="panel flex items-center gap-3 px-3.5 py-3">
            <FileIcon />
            <div className="min-w-0">
              <p className="truncate text-[14px] font-medium text-ink">{file.name}</p>
              <p className="num text-[12.5px] text-ink-muted">{formatBytes(file.size)}</p>
            </div>
          </div>
        )}

        {step.kind === 'ask' && (
          <ul className="panel mt-3 divide-y divide-line text-[13.5px] leading-relaxed text-ink-soft">
            <li className="flex gap-2 px-3.5 py-2.5">
              <Dot />
              <span>
                <span className="font-medium text-ink">Scan and upload</span> reads your name,
                title, roles and education into the profile, then lets you pick the skills to
                add.
              </span>
            </li>
            <li className="flex gap-2 px-3.5 py-2.5">
              <Dot />
              <span>
                <span className="font-medium text-ink">Upload only</span> stores the file and
                makes it your default, without changing your profile.
              </span>
            </li>
          </ul>
        )}

        {step.kind === 'uploading' && (
          <div className="mt-6 flex flex-col items-center gap-3 py-4 text-center" role="status">
            <svg className="h-8 w-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.2" />
              <path
                d="M22 12a10 10 0 0 0-10-10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <p className="text-[16px] text-ink">
              {step.scan ? 'Reading your resume…' : 'Uploading…'}
            </p>
            {step.scan && <p className="text-[14px] text-ink-muted">This takes 30–60 seconds.</p>}
          </div>
        )}

        {step.kind === 'review' && (
          <ReviewStep
            found={step.found}
            selected={selected}
            onChange={setSelected}
            summary={appliedSummary(step.result)}
          />
        )}

        {step.kind === 'failed' && (
          <div
            role="alert"
            className="rounded-field border border-danger/20 bg-danger/[0.06] px-4 py-3 text-[14px] leading-relaxed text-danger"
          >
            {step.message}
          </div>
        )}
      </div>
    </Modal>
  )
}

function ReviewStep({
  found,
  selected,
  onChange,
  summary,
}: {
  found: string[]
  selected: string[]
  onChange: (next: string[]) => void
  summary: string | null
}) {
  const removed = found.filter((name) => !selected.includes(name))
  return (
    <div>
      {found.length === 0 ? (
        <p className="rounded-field bg-surface-muted/70 px-4 py-3 text-[14px] text-ink-muted">
          Every skill in the resume is already on your profile.
        </p>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <p className="label-eyebrow">
              <span className="num">{selected.length}</span> of <span className="num">{found.length}</span> selected
            </p>
            {removed.length > 0 && (
              <button
                type="button"
                onClick={() => onChange(found)}
                className="text-[12.5px] font-medium text-ink-muted underline underline-offset-4 hover:text-ink"
              >
                Restore all
              </button>
            )}
          </div>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {selected.map((name) => (
              <li
                key={name}
                className="inline-flex h-9 max-w-full items-center gap-1 rounded-pill border border-brand/30 bg-brand-soft pl-3 pr-1.5 text-[13px] text-ink"
              >
                <span className="truncate">{name}</span>
                <button
                  type="button"
                  onClick={() => onChange(selected.filter((existing) => existing !== name))}
                  aria-label={`Don't add ${name}`}
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-brand/15 hover:text-ink"
                >
                  <svg className="h-2.5 w-2.5" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                    <path d="m2 2 6 6M8 2 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {summary && (
        <p className="mt-5 flex items-start gap-2 border-t border-line pt-4 text-[13px] leading-relaxed text-ink-muted">
          <CheckIcon />
          {summary}
        </p>
      )}
    </div>
  )
}

const FIELD_LABEL: Record<string, string> = {
  fullName: 'name',
  firstName: 'name',
  lastName: 'name',
  headline: 'headline',
  summary: 'summary',
  currentTitle: 'current title',
  currentCompany: 'current company',
  totalExperienceYears: 'years of experience',
  phone: 'phone',
  phoneNumber: 'phone',
  email: 'email',
  linkedinUrl: 'LinkedIn',
  githubUrl: 'GitHub',
  portfolioUrl: 'website',
  educationUniversity: 'education',
  educationCourse: 'education',
  educationGraduationYear: 'education',
  education: 'education',
}

/** `first_name` -> `firstName`, so one table covers both spellings. */
const toCamel = (field: string) => field.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())

/** "Also filled from your resume: name, current title, 4 roles, education." */
function appliedSummary(result: ResumeParseResult): string | null {
  const applied = result.applied
  if (!applied) return null
  const parts: string[] = []
  for (const field of applied.fields ?? []) {
    // Column names come back snake_case (`current_title`); humanise what the table misses.
    const label = FIELD_LABEL[field] ?? FIELD_LABEL[toCamel(field)] ?? humanise(field).toLowerCase()
    if (!parts.includes(label)) parts.push(label)
  }
  if (applied.experiences > 0) {
    parts.push(`${applied.experiences} role${applied.experiences === 1 ? '' : 's'}`)
  }
  if (applied.education && !parts.includes('education')) parts.push('education')
  if (applied.skills > 0) parts.push(`${applied.skills} skill${applied.skills === 1 ? '' : 's'}`)
  return parts.length ? `Also filled from your resume: ${parts.join(', ')}.` : null
}

function Dot() {
  return <span aria-hidden className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-ink-faint" />
}

function FileIcon() {
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] bg-surface text-ink-muted ring-1 ring-line">
      <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M4 1.5h5l3.5 3.5v9.5H4z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path d="M9 1.5V5h3.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

function CheckIcon() {
  return (
    <svg className="mt-[3px] h-3.5 w-3.5 shrink-0 text-brand" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m3 8.5 3.2 3L13 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
